import { classNames } from 'tradera/utils/class-names';
import type React from 'react';

import { Text } from 'tradera/components/atoms/text/text';
import AlertIcon from '@tradera/blueprint/theme/icons2/alert.svg?jsx';

export type BaseInputWrapperProps = {
    label?: React.ReactNode;
    hint?: React.ReactNode;
    // A string or ReactNode can be used as the error message and error styling, a boolean will only give error styling
    error?: boolean | React.ReactNode;
    help?: React.ReactNode;
    name?: string;
    children: React.ReactNode;
    /**
     * This will collapse the hint/error area when there's no info.
     * By default it is not collapsed, and will always take up space that
     * is needed for the error message and hint, if any.
     */
    collapsed?: boolean;
    className?: string;
    style?: React.CSSProperties;
    /**
     * True if the input is pristine, i.e. has not been changed from the initial state.
     */
    isPristine?: boolean;
    /**
     * Supress the error message when the input is pristine.
     * Sometimes you don't want to show an error message before the user has even
     * started interacting with the input.
     */
    supressErrorWhenPristine?: boolean;
};

const isVisibleNode = (error: BaseInputWrapperProps['error']) => {
    return (
        (typeof error === 'string' && !!error) ||
        (typeof error === 'object' && error !== null)
    );
};

export const BaseInputWrapper = ({
    label,
    error,
    hint,
    help,
    name,
    collapsed,
    className,
    style,
    isPristine,
    supressErrorWhenPristine,
    children
}: BaseInputWrapperProps) => {
    const supressError = supressErrorWhenPristine && isPristine;

    const textBelow =
        isVisibleNode(error) && !supressError ? (
            error
        ) : isVisibleNode(hint) ? (
            hint
        ) : // &nbsp; is to make sure that this element always has a height,
        // otherwise the form will jump when the error message is shown, if
        // no hint is provided
        collapsed ? (
            ''
        ) : (
            <>&nbsp;</>
        );

    const hasLabelOrHelp = !!label || !!help;

    return (
        <div className={classNames(className)} style={style}>
            {hasLabelOrHelp ? (
                <div className="d-flex align-items-center justify-content-between mb-1">
                    <Text as="label" font="hansen" htmlFor={name}>
                        {label}
                    </Text>
                    <Text
                        as="span"
                        color="gray-600"
                        fontWeight="medium"
                        size="oslo">
                        {help}
                    </Text>
                </div>
            ) : null}
            {children}
            {textBelow ? (
                error && !supressError ? (
                    <FormFieldErrorMessage className="mt-1">
                        {textBelow}
                    </FormFieldErrorMessage>
                ) : (
                    <Text
                        color="gray-600"
                        size="oslo"
                        fontWeight="medium"
                        className="mt-1">
                        {textBelow}
                    </Text>
                )
            ) : null}
        </div>
    );
};

export const FormFieldErrorMessage = ({
    children,
    className
}: React.PropsWithChildren & { className?: string }) => (
    <Text
        // TODO: maybe only alert if there is an error?
        role="alert"
        color="danger"
        size="oslo"
        fontWeight="medium"
        className={classNames(className, 'd-flex align-items-start')}>
        <AlertIcon className="icon icon-xs mr-1" />
        {children}
    </Text>
);
